import { tEngine } from "../../../i18n/engine";
import { t } from "../../../i18n";
import { CASE_LABEL, fmt, type CaseId, type WallResult } from "@tasduvar/engine";
import { CheckTable, Section, Verdict, type CheckRowData } from "./common";

const rowsOf = (c: NonNullable<WallResult["cases"]["static"]>): CheckRowData[] => [
  { label: t("Devrilme"), check: c.overturning, unit: "kNm/m" },
  { label: t("Kayma"), check: c.sliding, unit: "kN/m" },
  { label: t("Taşıma gücü"), check: c.bearing, unit: "kN/m²" },
];

export function StabilityTbdy({ result }: { result: WallResult }): JSX.Element {
  if (result.designCode === "us") {
    return (
      <Section id="stabilityTbdy" title={t("9. Yönetmelik Doğrulaması (ABD — IBC 2021 §1807.2 / AASHTO LRFD §11.6)")}>
        <p className="muted">{t("ABD profilinde doğrulama yalnız klasik güvenlik katsayılarıyla yapılır (bölüm 10): devrilme ve kayma GK ≥ 1,5 (depremde 1,1), taşıma gücü GK 3, bileşke B/6 (depremde B/3) içinde.")}</p>
      </Section>
    );
  }
  if (result.designCode === "ec7") {
    return (
      <Section id="stabilityTbdy" title={t("9. Eurocode 7 Kısmi Katsayılı Doğrulama (EN 1997-1 §2.4.7, Ek A)")}>
        <p className="muted">{t("E_d ≤ R_d. Etkiler γG (aleyhte) / γG,fav (lehte) ve γQ; malzeme tanφ/γφ, c/γc; dirençler γRh, γRv, γRe. Devrilme EQU, kayma ve taşıma GEO sınır durumları. Tek kaynak ilkesi: itkinin düşey bileşeni kayma/devrilmede lehte, taşımada aleyhte.")}</p>
        {(Object.keys(result.cases) as CaseId[]).map((cid) => { const c = result.cases[cid]!; return (
          <div key={cid}>
            <h3>{tEngine(CASE_LABEL[cid])}</h3>
            <table>
              <thead><tr><th>{t("Kombinasyon")}</th><th className="num">γG / γG,fav / γQ</th><th className="num">γφ / γc</th><th className="num">φd (°)</th><th className="num">{t("Devrilme E/R")}</th><th className="num">{t("Kayma E/R")}</th><th className="num">{t("Taşıma E/R")}</th><th>{t("Sonuç")}</th></tr></thead>
              <tbody>{(c.ec ?? []).map((r) => (
                <tr key={r.id}><td>{r.id}</td><td className="num">{fmt(r.factors.gG, 2)} / {fmt(r.factors.gGfav, 2)} / {fmt(r.factors.gQ, 2)}</td><td className="num">{fmt(r.factors.gPhi, 2)} / {fmt(r.factors.gC, 2)}</td><td className="num">{fmt(r.phiD, 1)}</td>
                  <td className="num">{fmt(r.overturning.Ed)} / {fmt(r.overturning.Rd)} = {fmt(r.overturning.ratio, 2)}</td><td className="num">{fmt(r.sliding.Ed)} / {fmt(r.sliding.Rd)} = {fmt(r.sliding.ratio, 2)}</td><td className="num">{fmt(r.bearing.Ed)} / {fmt(r.bearing.Rd)} = {fmt(r.bearing.ratio, 2)}</td>
                  <td><Verdict ok={r.overturning.ok && r.sliding.ok && r.bearing.ok} /></td></tr>
              ))}</tbody>
            </table>
          </div>
        ); })}
      </Section>
    );
  }
  return (
    <Section id="stabilityTbdy" title={t("9. TBDY 2018 Stabilite Kontrolleri (§16.12.1, Denk. 16.6–16.21)")}>
      <p className="muted">{t("Etki ≤ Direnç / γR. Devrilme γRdev, kayma γRh = 1,1 (pasif direncin %30'u γRp = 1,4 ile), taşıma gücü γRv = 1,4.")}</p>
      {(Object.keys(result.cases) as CaseId[]).map((cid) => <CheckTable key={cid} caseLabel={tEngine(CASE_LABEL[cid])} rows={rowsOf(result.cases[cid]!)} mode="tbdy" />)}
    </Section>
  );
}

export function StabilityClassic({ result }: { result: WallResult }): JSX.Element {
  return (
    <Section id="stabilityClassic" title={t("10. Klasik Güvenlik Katsayıları")}>
      {Object.values(result.cases).some((c) => c?.overturning.classic.informative || c?.sliding.classic.informative) ? <p className="muted">{t("Devrilme ve kayma GK bilgi amaçlıdır; hüküm bölüm 9'daki TBDY dayanım katsayılarıyla verilir. Taban dış merkezliliği en çok B/3 alınır; taşıma gücünde q ≤ σem sürer.")}</p> : null}
      {(Object.keys(result.cases) as CaseId[]).map((cid) => { const c = result.cases[cid]!; const e = c.eccentricity; return (
        <div key={cid}>
          <CheckTable caseLabel={tEngine(CASE_LABEL[cid])} rows={rowsOf(c)} mode="classic" />
          <p>{t("Dış merkezlilik: e = |B/2 − x̄| =")} {fmt(Math.abs(e.e), 3)} m {e.ok ? "≤" : ">"} B/{e.limitDivisor} = {fmt(e.limit, 3)} m → <Verdict ok={e.ok} /></p>
        </div>
      ); })}
    </Section>
  );
}

export function BasePressureSection({ result, detailed }: { result: WallResult; detailed: boolean }): JSX.Element {
  return (
    <Section id="basePressure" title={t("11. Dış Merkezlilik, Taban Gerilmeleri ve Taşıma Gücü")}>
      <table>
        <thead><tr><th>{t("Durum")}</th><th className="num">{t("N (kN/m)")}</th><th className="num">x̄ (m)</th><th className="num">e (m)</th><th className="num">B′ (m)</th><th className="num">{t("q_burun")}</th><th className="num">{t("q_topuk")}</th><th className="num">q_o = N/B′</th><th className="num">q_k</th><th>{t("Sonuç")}</th></tr></thead>
        <tbody>
          {(Object.keys(result.cases) as CaseId[]).map((cid) => { const c = result.cases[cid]!; return (
            <tr key={cid}><td>{tEngine(CASE_LABEL[cid])}</td><td className="num">{fmt(c.sums.N)}</td><td className="num">{fmt(c.eccentricity.xbar, 3)}</td><td className="num">{fmt(c.eccentricity.e, 3)}</td><td className="num">{fmt(c.eccentricity.Beff, 3)}</td><td className="num">{fmt(c.basePressure.qToe)}</td><td className="num">{fmt(c.basePressure.qHeel)}{c.basePressure.tensionRedistributed ? " *" : ""}</td><td className="num">{fmt(c.basePressure.qMeyerhof)}</td><td className="num">{fmt(c.bearing.qk, 0)}</td><td><Verdict ok={c.bearing.classic.ok && (c.bearing.tbdy?.ok ?? true)} /></td></tr>
          ); })}
        </tbody>
      </table>
      <p className="muted">{t("Gerilmeler kN/m². * Bileşke çekirdek dışında; çekmesiz üçgen dağılım (temas uzunluğu 3·(B/2 − e)). q_k kaynağı:")} {result.cases.static?.bearing.qkSource === "computed" ? t("TBDY Denk. 16.8a ile hesaplandı") : t("zemin etüdünden alındı")}.</p>
      {detailed && result.cases.static?.bearing.qkSource === "computed" ? <p className="mono" style={{ fontSize: "8.5pt" }}>{t("q_k = c·N_c·d_c·i_c + q̄·N_q·d_q·i_q + ½·γ·B′·N_γ·d_γ·i_γ (Denk. 16.8a); ayrıntı için hesap adımları bölümüne bakınız.")}</p> : null}
    </Section>
  );
}
